import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

const inp = "w-full border border-rule px-3 py-2 text-sm focus:outline-none focus:border-klein bg-white";
const label = "block font-mono text-[10px] uppercase tracking-wider text-quiet mb-1";

export default function SystemNotifications() {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [cfg, setCfg] = useState({
    notifications_enabled: true,
    notifications_recipient: "",
    host: "",
    port: 587,
    username: "",
    sender_email: "",
    password: "",
  });
  const [savedAlready, setSavedAlready] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/system/notifications");
        setCfg({
          notifications_enabled: !!r.data.notifications_enabled,
          notifications_recipient: r.data.notifications_recipient || "",
          host: r.data.host || "",
          port: r.data.port || 587,
          username: r.data.username || "",
          sender_email: r.data.sender_email || "",
          password: "",
        });
        setSavedAlready(!!r.data.configured);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load settings");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (!user?.is_super_admin) {
    return (
      <div className="p-8 text-quiet" data-testid="not-allowed">
        Only the super admin can configure internal notifications.
      </div>
    );
  }
  if (loading) return <div className="p-8 text-quiet">Loading…</div>;

  const set = (k) => (e) => setCfg({ ...cfg, [k]: e.target.value });

  const save = async () => {
    if (!cfg.notifications_recipient || !cfg.host || !cfg.username || !cfg.sender_email) {
      return toast.error("All fields except password (if already saved) are required");
    }
    if (!savedAlready && !cfg.password) {
      return toast.error("Password is required on first save");
    }
    setBusy(true);
    try {
      const payload = { ...cfg, port: Number(cfg.port) };
      if (savedAlready && !cfg.password) {
        delete payload.password; // keep existing encrypted password
      }
      const r = await api.put("/system/notifications", payload);
      toast.success("Settings saved");
      setSavedAlready(!!r.data.configured);
      setCfg({ ...cfg, password: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const r = await api.post("/system/notifications/test");
      if (r.data.success) toast.success(`Test sent to ${cfg.notifications_recipient}`);
      else toast.error(r.data.message || "Test failed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Test failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-8 max-w-3xl" data-testid="system-notifications-page">
      <div className="border-b border-rule pb-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-quiet">Settings · Super Admin</div>
        <h1 className="font-serif text-5xl text-ink mt-2 leading-none">Internal notifications.</h1>
        <p className="text-sm text-quiet mt-3 max-w-xl">
          One inbox that receives every requisition + candidate event in the system. All active HR users
          are Cc&apos;d, and originators (e.g. the user who raised a requisition) are Cc&apos;d on related events.
        </p>
      </div>

      <div className="mt-8 bg-white border border-rule p-6 space-y-5">
        <label className="flex items-center gap-3 cursor-pointer" data-testid="toggle-enabled">
          <input
            type="checkbox"
            checked={cfg.notifications_enabled}
            onChange={(e) => setCfg({ ...cfg, notifications_enabled: e.target.checked })}
          />
          <span className="text-sm text-ink">Notifications enabled</span>
        </label>

        <div>
          <div className={label}>Notifications inbox (To)</div>
          <input
            data-testid="input-recipient"
            type="email"
            value={cfg.notifications_recipient}
            onChange={set("notifications_recipient")}
            placeholder="humanresources@gomo-dbs.com"
            className={inp}
          />
        </div>

        <div className="pt-4 border-t border-rule">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-quiet mb-3">SMTP credentials</div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className={label}>Host</div>
              <input data-testid="input-host" value={cfg.host} onChange={set("host")} placeholder="smtp.gmail.com" className={inp} />
            </div>
            <div>
              <div className={label}>Port</div>
              <input data-testid="input-port" type="number" value={cfg.port} onChange={set("port")} className={inp} />
            </div>
            <div>
              <div className={label}>Username</div>
              <input data-testid="input-username" value={cfg.username} onChange={set("username")} className={inp} />
            </div>
            <div>
              <div className={label}>Sender email (From)</div>
              <input data-testid="input-sender" type="email" value={cfg.sender_email} onChange={set("sender_email")} className={inp} />
            </div>
            <div className="col-span-2">
              <div className={label}>
                Password / app-password {savedAlready && <span className="text-quiet normal-case">· leave blank to keep existing</span>}
              </div>
              <input
                data-testid="input-password"
                type="password"
                value={cfg.password}
                onChange={set("password")}
                placeholder={savedAlready ? "••••••••" : "Required on first save"}
                className={inp}
              />
            </div>
          </div>
        </div>

        <div className="flex gap-3 pt-4 border-t border-rule">
          <button
            data-testid="save-btn"
            disabled={busy}
            onClick={save}
            className="bg-ink hover:bg-black text-white px-5 py-2 text-sm font-mono uppercase tracking-wider disabled:opacity-50"
          >
            Save
          </button>
          <button
            data-testid="test-btn"
            disabled={busy || !savedAlready}
            onClick={sendTest}
            title={savedAlready ? "" : "Save first"}
            className="border border-ink text-ink hover:bg-paper px-5 py-2 text-sm font-mono uppercase tracking-wider disabled:opacity-50"
          >
            Send test
          </button>
        </div>
      </div>

      <div className="mt-8 bg-paper border border-rule p-5">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-quiet mb-2">What gets notified</div>
        <ul className="text-sm text-ink space-y-1.5 list-disc pl-5">
          <li><b>Requisitions</b> — raised, accepted, rejected, closed, CTC revised. Originator is Cc&apos;d.</li>
          <li><b>Candidates</b> — applied, AI-screened, interview scheduled / rescheduled, decision (proceed / reject / select), offer drafted.</li>
          <li><b>Team feed</b> — every active HR user is Cc&apos;d on every event.</li>
        </ul>
      </div>
    </div>
  );
}
